import React from 'react'
import { Alert, Anchor, Stack, Text } from '@mantine/core'

import {
  CA_NOTICE_CHECKLIST_URL,
  getStateRecordScript,
} from '@/components/Event/CrForm/stateRecordScripts'
import { Event, Reporter } from '@/types'

export const ClientReporterInfoAlert: React.FC<{
  event: Event
  reporter: Reporter
}> = ({ event, reporter }) => {
  const stateScript = getStateRecordScript(event)

  if (stateScript) {
    return (
      <Alert title="">
        <strong>{stateScript.stateName} event:</strong> you perform the read-on
        and call the record. Don&apos;t wait for the Court Reporter to ask. The{' '}
        {stateScript.stateName} language is in the script below.
        {event.case.state === 'CA' && (
          <>
            {' '}
            Before the event, review the notice with the{' '}
            <Anchor
              href={CA_NOTICE_CHECKLIST_URL}
              target="_blank"
              rel="noreferrer"
              component="a"
            >
              CA notice checklist
            </Anchor>
            .
          </>
        )}
      </Alert>
    )
  }

  return (
    <>
      <Stack>
        <Alert title="">
          <div>
            <strong>Info:</strong> This is a Session Assistant event where you
            are performing administrative tasks and allowing the Certified Court
            Report to own the record. This CR may not have worked with Filevine
            previously. If they have questions, you may consult this{' '}
            <Anchor
              href="https://dbf-digital-reporters.helpscoutdocs.com/article/505-what-to-expect-client-preferred-court-reporters"
              target="_blank"
              rel="noreferrer"
              component="a"
            >
              help article
            </Anchor>{' '}
            to assist in answering.
            <br />
            <br />
            <strong>Your responsibilities:</strong>
            <ol
              style={{
                marginTop: 4,
                marginBottom: 4,
                paddingLeft: 20,
                listStyle: 'decimal',
              }}
            >
              <li>Start the Zoom meeting</li>
              <li>
                Rename yourself to <em>Filevine - Session Assistant</em>
              </li>
              <li>
                <Anchor
                  href="https://dbf-digital-reporters.helpscoutdocs.com/article/498-assigning-host-and-co-host-roles"
                  target="_blank"
                  rel="noreferrer"
                  component="a"
                >
                  Assign the reporter as co-host
                </Anchor>{' '}
                if requested
              </li>
              <li>Record attendance with accurate spellings</li>
              <li>Spotlight the witness</li>
              <li>Take notes with general timestamps</li>
              <li>Close out the event</li>
            </ol>
            <br />
            <strong>
              You do <u>NOT</u> need to:
            </strong>
            <ol
              style={{
                marginTop: 4,
                marginBottom: 4,
                paddingLeft: 20,
                listStyle: 'decimal',
              }}
            >
              <li>Call the record</li>
              <li>Identify attendees on the record</li>
              <li>Swear in the witness</li>
              <li>Ask about read/waive and transcript orders</li>
            </ol>
            <br />
            <span>
              You can refer to this{' '}
              <Anchor
                href="https://dbf-digital-reporters.helpscoutdocs.com/article/497-client-provided-court-reporter-event"
                target="_blank"
                rel="noreferrer"
                component="a"
              >
                article
              </Anchor>{' '}
              to learn more about running a Session Assistant Event with a
              Client Provided Court Reporter.
            </span>
          </div>
        </Alert>
        <Alert title="Here is a suggested script to welcome the CR:">
          <Text
            size="sm"
            color="gray.9"
            sx={{
              fontFamily: 'Aeonik Mono Medium, monospace',
            }}
          >
            Good morning/afternoon, my name is {reporter.name} and I am the
            Session Assistant here on behalf of Filevine. I am here to run the
            Zoom and perform more administrative tasks to allow you to focus
            completely on owning the record.
          </Text>
        </Alert>
      </Stack>
    </>
  )
}
